<template>
  <main class="page-wrapper">
    <div v-if="loading">
      <spinner />
    </div>
    <div v-else class="base">
      <div class="flex header-container">
        <div style="display: flex; flex: 1">
          <img src="/assets/color-logo.svg" alt class="logo" />
          <div class="header">
            <div style="display: flex; flex-direction: column; align-items: flex-end; gap: 7px">
              <div class="receipt-header bold fontsize5">
                <span v-if="isR1">R1 {{ $t('invoiceNumber') }}</span>
                <span v-else>{{ $t('invoiceNumber') }}</span>
              </div>
              <div style="display: flex; flex-direction: column; align-items: flex-end; gap: 3px">
                <div class="receipt-date-label fontsize2">{{ $t('invoiceDate') }}</div>
                <div v-if="isR1" class="receipt-date-label fontsize2">
                  {{ $t('paymentDeadline') }}
                </div>
              </div>
            </div>
            <div class="receipt-value-container">
              <div class="fontsize5">
                {{ invoiceNumber }}
              </div>
              <div class="receipt-date-value-container">
                <div class="receipt-date-label fontsize2">
                  {{ formatDateTime(date, $i18next.language) }}
                </div>
                <div v-if="isR1" class="receipt-date-label fontsize2">{{ dueDate }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="flex body">
        <div>
          <div class="fontsize1">{{ $t('invoiceIssued') }}</div>
          <div class="receipt-owner-name bold fontsize3">CoreEvent d.o.o.</div>
          <div class="receipt-owner-adress fontsize3">Dunjevac 15, 10000 Zagreb Hrvatska</div>
          <div class="receipt-owner-oib-header fontsize1">
            {{ $t('vat') }}: <span class="receipt-owner-oib fontsize4">36611335369</span>
          </div>
        </div>
        <div class="buyer-info-container">
          <div class="fontsize1">{{ $t('customer') }}</div>
          <div class="buyer-info bold fontsize3">
            {{ isR1 ? legalEntity.name : $t('retailCustomer') }}
          </div>
          <template v-if="isR1">
            <div class="customer-owner-address fontsize3">
              {{ legalEntity.address }}, {{ legalEntity.zipCode }} {{ legalEntity.city }}
              {{ legalEntity.country }}
            </div>
            <div class="customer-owner-oib-header fontsize1">
              {{ $t('vat') }}:
              <span class="customer-owner-oib fontsize4">{{ legalEntity.OIB }}</span>
            </div>
          </template>
        </div>
      </div>
      <div v-if="event" class="event-info">
        <p>
          {{ $t('event') }}: <b>{{ event.title }}</b>
        </p>
        <p>
          {{ $t('eventDate') }}:
          <strong
            >{{ moment(event.from).format('DD.MM.YYYY') }}{{ $t('at')
            }}{{ moment(event.from).format('HH:mm [h]') }}</strong
          >
        </p>
      </div>
      <div class="flex table-container" style="margin-top: 1rem">
        <div class="table-header bold fontsize1">
          <div>{{ $t('inoviceNum') }}</div>
          <div>{{ $t('productDescription') }}</div>
          <div>{{ $t('unit') }}</div>
          <div>{{ $t('amount') }}</div>
          <div>{{ $t('price') }}</div>
          <div>{{ $t('tax') }}</div>
          <div>{{ $t('itemAmount') }}</div>
        </div>
        <div
          v-for="(item, name, index) in invoiceItems"
          :key="`${item[0].id}/${index}`"
          class="table-body"
        >
          <div class="table-body-item fontsize2">
            <div>* {{ index + 1 }}</div>
            <div>{{ item[0].orderItem.name }}</div>
            <div>{{ $t('pieces') }}</div>
            <div>{{ item.length }}</div>
            <div>
              {{ Number(item[0].orderItem.priceNoVAT).toFixed(2) }}
            </div>
            <div>
              {{
                orderItemsWith25Vat.includes(parseInt(item[0].orderItem.id))
                  ? '25'
                  : Number(VATPercentage).toFixed(0)
              }}%
            </div>
            <div>
              {{ (item[0].orderItem.priceNoVAT * item.length).toFixed(2) }}
            </div>
          </div>
        </div>
        <div v-if="fixedFeeItems.length">
          <div
            v-for="(fee, index) in fixedFeeItems"
            :key="index + 1"
            class="table-body-item fontsize2"
          >
            <div>{{ Object.keys(invoiceItems).length + index + 1 }}</div>
            <div>{{ fee.name }}</div>
            <div>{{ $t('pieces') }}</div>
            <div>{{ fee.count }}</div>
            <div>
              {{ fee.singleItemPriceNoVAT.toFixed(2) }}
            </div>
            <div>25%</div>
            <div>
              {{ (fee.singleItemPriceNoVAT * fee.count).toFixed(2) }}
            </div>
          </div>
        </div>
      </div>
      <div class="total-container fontsize2">
        <div class="total">
          <div class="total-label-style">{{ $t('totalNoTax') }}:</div>
          <div class="total-value-container">
            <div class="fontsize2">{{ totalNoVAT.toFixed(2) }} €</div>
          </div>
        </div>
        <div class="total">
          <div class="total-label-style">
            {{ $t('tax') }} ({{ Number(VATPercentage).toFixed(0) }}%), {{ $t('base') }}
            {{ totalTicketsNoVAT.toFixed(2) }} €
          </div>
          <div class="total-value-container">
            <div class="fontsize2">{{ (totalTickets - totalTicketsNoVAT).toFixed(2) }} €</div>
          </div>
        </div>
        <div v-if="fixedFeeItems.length" class="total">
          <div class="total-label-style">
            {{ $t('tax') }} ({{ coreEventFeeVAT * 100 }}%), {{ $t('base') }}
            {{ totalCoreEventFeeNoVat.toFixed(2) }} €
          </div>
          <div class="total-value-container">
            <div class="fontsize2">
              {{ round(totalCoreEventFee - totalCoreEventFeeNoVat, 2).toFixed(2) }} €
            </div>
          </div>
        </div>
        <div v-if="promoCodeDiscount" class="total">
          <div class="total-label-style">
            {{ $t('promoCodeDiscount') }}
          </div>
          <div class="total-value-container">
            <div class="fontsize2">-{{ promoCodeDiscount }} €</div>
          </div>
        </div>
        <div v-if="appliedGiftCardAmount" class="total">
          <div class="total-label-style">
            {{ $t('giftCardDiscount') }}
          </div>
          <div class="total-value-container">
            <div class="fontsize2">-{{ appliedGiftCardAmount }} €</div>
          </div>
        </div>
        <div class="total">
          <div class="total-label-style">{{ $t('inTotal') }}:</div>
          <div class="total-value-container">
            <div class="fontsize4 bold">{{ totalWithVAT.toFixed(2) }} €</div>
          </div>
        </div>
      </div>
      <div class="info-container fontsize1">
        <div class="info-item">
          <div class="info-item-label">{{ $t('paymentMethod') }}</div>
          <div class="info-item-value fontsize2">{{ $t('cardPayment') }}</div>
        </div>
        <div class="info-item">
          <div class="info-item-label">{{ $t('operator') }}</div>
          <div class="info-item-value fontsize2">Filip Gobin</div>
        </div>
        <div class="info-item">
          <div class="info-item-label">{{ $t('notes') }}</div>
          <div class="info-item-value fontsize2">
            * {{ $t('bulledInName') }} : {{ organizerLegalName }} , {{ organizerAddress }},
            {{ $t('vat') }}: {{ organizerOIB }}
          </div>
        </div>
      </div>
      <div v-if="fiscalisationInfo" style="display: flex; flex-direction: column; gap: 0.25rem">
        <p class="info-item-label fontsize2">ZKI: {{ fiscalisationInfo.zki }}</p>
        <p class="info-item-label fontsize2">JIR: {{ fiscalisationInfo.jir }}</p>
        <img id="qrcode" alt="QR code" />
      </div>
      <div class="footer">
        <div class="info-data-container fontsize1">
          <div class="info-item-container">
            <div>{{ $t('ourAddresses') }}</div>
            <div>Dunjevac 15, 10000 Zagreb, Hrvatska</div>
            <div>Internet: https://core-event.co/</div>
          </div>
          <div class="info-item-container">
            <div>Pravni podaci</div>
            <div>{{ $t('vat') }}: 36611335369</div>
            <div>IBAN: HR5223400091111141277</div>
            <div>SWIFT: PBZGHR2X</div>
          </div>
          <div class="info-item-container">
            <div>{{ $t('contacts') }}</div>
            <div>E-mail: support@core-event.co</div>
          </div>
        </div>
        <div class="bottom-info-container fontsize1">
          {{ $t('companyRegistered') }}
        </div>
      </div>
    </div>
    <language-switcher id="language-switcher" :event-slug="eventSlug" :is-zkm="false" />
  </main>
</template>

<script>
import moment from 'moment'
import { round } from 'lodash'
import { GET_ORDER_FOR_INVOICE_BY_UUID } from '@/graphql/order.graphql'
import i18next from 'i18next'
import QRCode from 'qrcode'
import LanguageSwitcher from '@/components/shared/language-switcher.vue'
import { CoreEventFeeType, OrderItemType, OrganizationType, PromoCodeDiscountType } from '@/types'
import { convertUtcToCroatianLocalTime } from '@/utils'

export default {
  components: {
    LanguageSwitcher,
  },
  props: {
    orderUUID: String,
  },
  data() {
    return {
      isR1: false,
      loading: true,
      invoiceNumber: '',
      date: '',
      dueDate: '',
      organization: {},
      invoiceItems: [{}],
      totalNoVAT: 0,
      fixedFeeItems: [],
      numberOfPurchasedTickets: 0,
      numberOfNonGratisPurchasedTickets: 0,
      VATPercentage: 0,
      VATAmount: 0,
      totalCoreEventFee: 0,
      totalTicketsNoVAT: 0,
      totalTickets: 0,
      fiscalisationInfo: null,
      totalCoreEventFeeNoVat: 0,
      orderItemsWith25Vat: [1088, 1089],
      coreEventFeeVAT: 0.25,
      totalWithVAT: 0,
      organizerOIB: null,
      referenceNumber: '',
      organizerLegalName: '',
      eventSlug: null,
      organizerAddress: '',
      legalEntity: null,
      appliedGiftCardAmount: null,
      promoCodeDiscount: 0,
      event: null,
    }
  },
  methods: {
    moment,
    async generateQrCode(data, totalPrice) {
      setTimeout(async () => {
        const img = document.getElementById('qrcode')
        img.src = await QRCode.toDataURL(
          `https://porezna.gov.hr/rn?jir=${data.jir}&datv=${this.date}&izn=${totalPrice * 100}`,
          { margin: 0 },
        )
      }, 0)
    },
    formatDate(date) {
      return moment(date).format('DD. MM. YYYY')
    },
    formatDateTime(date, language) {
      return moment(date, 'YYYYMMDD_HHmm').format(
        `DD.MM.YYYY. ${language === 'hr' ? '[u]' : '[at]'} HH:mm`,
      )
    },
    round(amount = 0) {
      return round(amount, 2)
    },
    calculatePromoCodeDiscount(purchases) {
      return purchases.reduce((acc, curr) => {
        if (!curr.promoCode) {
          return acc
        }

        if (curr.promoCode.discountType === PromoCodeDiscountType.AMOUNT) {
          return acc + Number(curr.promoCode.discountAmount)
        }

        return (
          acc + (Number(curr.promoCode.discountPercentage) / 100) * Number(curr.orderItem.price)
        )
      }, 0)
    },
    calculateNumberOfNonGratisTickets(purchases) {
      return purchases.filter(p => {
        let price = Number(p.orderItem.price)

        if (p.promoCode) {
          price -=
            p.promoCode.discountType === PromoCodeDiscountType.AMOUNT
              ? p.promoCode.discountAmount
              : round((Number(p.orderItem.price) * p.promoCode.discountPercentage) / 100, 2)
        }

        return price > 0
      }).length
    },
    isAnnualSportTicket(organization, purchases) {
      return (
        organization.type === OrganizationType.SPORT_ORGANIZATION &&
        purchases.some(purchase => purchase.prepaidCouponUserId)
      )
    },
  },
  apollo: {
    getOrderForInvoiceByUUID: {
      query: GET_ORDER_FOR_INVOICE_BY_UUID,
      variables() {
        return {
          uuid: this.orderUUID,
        }
      },
      manual: true,
      result({ data, loading }) {
        if (loading || !data.getOrderForInvoiceByUUID) {
          return
        }
        const {
          getOrderForInvoiceByUUID: {
            price,
            createdAt,
            invoiceNumber,
            event,
            fiscalisationInfo,
            legalEntity,
            giftCardId,
            giftCardAmount,
          },
        } = data
        let {
          getOrderForInvoiceByUUID: { purchases },
        } = data
        this.eventSlug = event ? event.slug : null
        this.event = event
        const organization = (event && event.organization) || purchases[0].orderItem.organization
        this.organization = organization
        this.legalEntity = legalEntity
        this.isR1 = !!legalEntity

        if (this.isAnnualSportTicket(organization, purchases)) {
          purchases = purchases.filter(
            purchase => purchase.orderItem.type === OrderItemType.PREPAID_COUPON,
          )
        }
        let totalFeeAmount = 0
        let totalFeeAmountNoVat = 0
        this.fixedFeeItems =
          fiscalisationInfo && fiscalisationInfo.fiscalisationError
            ? []
            : (
                (fiscalisationInfo &&
                  fiscalisationInfo['full_receipt'] &&
                  fiscalisationInfo['full_receipt'].items) ||
                []
              )
                .map(i => {
                  if (i.name.includes(CoreEventFeeType.TICKET)) {
                    totalFeeAmount += i.price / 100
                    totalFeeAmountNoVat += i.price / 100 / 1.25

                    return {
                      name: i.name,
                      count: i.amt / 1000,
                      totalPrice: i.price / 100,
                      singleItemPrice: i['single_item_price'] / 100,
                      singleItemPriceNoVAT: Number(i['single_item_price'] / 100) / 1.25,
                    }
                  }
                })
                .filter(Boolean)
        this.invoiceNumber = `${invoiceNumber}-1-${
          moment(createdAt).isAfter('2024/2/26 23:59') ? 12 : 10
        }`
        this.VATPercentage = organization.VATPercentage || 0

        let publishTime
        if (fiscalisationInfo && fiscalisationInfo.publishTime) {
          publishTime = fiscalisationInfo.publishTime
        } else {
          publishTime = moment(createdAt).format('YYYYMMDD_HHmm')
        }
        this.date = convertUtcToCroatianLocalTime(publishTime, 'YYYYMMDD_HHmm')
        this.numberOfPurchasedTickets = purchases.length
        this.numberOfNonGratisPurchasedTickets = this.calculateNumberOfNonGratisTickets(purchases)
        for (const purchase of purchases) {
          purchase.orderItem.priceNoVAT =
            purchase.orderItem.price / (1 + (this.VATPercentage || 0) / 100)
          console.log(purchase.orderItem.price, round(purchase.orderItem.price / 1.05, 2))
        }

        this.totalCoreEventFee = round(totalFeeAmount, 2)
        i18next.changeLanguage(organization.preferredLanguage == 'en' ? 'en' : 'hr')
        this.promoCodeDiscount = this.calculatePromoCodeDiscount(purchases)
        this.totalWithVAT = Number(price.split(' ')[1])

        this.totalTicketsNoVAT = purchases.reduce((acc, item) => {
          return acc + item.orderItem.priceNoVAT
        }, 0)
        this.totalTickets = round(
          this.totalWithVAT + this.promoCodeDiscount - this.totalCoreEventFee,
          2,
        )
        this.totalNoVAT = this.totalTicketsNoVAT
        if (this.totalCoreEventFee) {
          this.totalCoreEventFeeNoVat = totalFeeAmountNoVat
          this.totalNoVAT = round(this.totalCoreEventFeeNoVat + this.totalNoVAT, 2)
        }
        this.VATAmount = round(this.totalWithVAT - this.totalNoVAT, 2)

        // if gift card is applied, price is reduced by gift card amount
        if (giftCardId) {
          this.appliedGiftCardAmount = giftCardAmount.split(' ')[1] || '0.00'
          this.totalWithVAT = Number(this.totalWithVAT) - Number(this.appliedGiftCardAmount)
        }

        this.invoiceItems = _.groupBy(purchases, p => p.orderItemId)

        this.organizerLegalName = organization.legalName
        this.organizerAddress = organization.address
        this.organizerOIB = organization.OIB

        if (fiscalisationInfo) {
          this.fiscalisationInfo = fiscalisationInfo
          this.generateQrCode(this.fiscalisationInfo, this.totalWithVAT)
        }
        this.loading = false
      },
    },
  },
}
</script>

<style scoped>
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

@page {
  size: A4;
  margin: 0;
}

@media print {
  @page {
    size: A4;
    margin: 0;
  }

  body {
    margin: 1.6cm;
  }
}

html,
body {
  height: 100%;
}

main {
  height: 297mm;
  padding: 0.33333in 0.22222in 0.33333in 0.44444in;
  width: 210mm;
}

header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.33333in;
}

hr {
  margin-bottom: 0.625rem;
}

.logo {
  height: 0.66666in;
}

.page-wrapper {
  display: flex;
  flex-direction: column-reverse;
}

#language-switcher {
  align-self: end;
}

.event-info {
  margin-top: 1rem;
  font-family: OpenSans;
  font-size: 0.7rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  text-indent: 8px;
}

@media print {
  #language-switcher {
    display: none;
  }
}

.flex {
  display: flex;
  /*  background-color:rgba(99, 99, 99, 0.2);  */
}

.base {
  font-family: OpenSans;
  font-weight: normal;
  line-height: 1;
  letter-spacing: 0;
  unicode-bidi: bidi-override;
  -moz-font-feature-settings: 'liga' 0;
  height: 100%;
  position: relative;
}

.bold {
  font-weight: bold;
  line-height: 1;
  word-spacing: 0;
}

.fontsize1 {
  font-size: 0.5rem;
}

.fontsize2 {
  font-size: 0.6rem;
}

.fontsize3 {
  font-size: 0.65rem;
}

.fontsize4 {
  font-size: 0.7rem;
}

.fontsize5 {
  font-size: 1rem;
}

.gray {
  color: rgb(102 102 102);
}

@font-face {
  font-family: OpenSans;
  src: url('./fonts/OpenSans-vf.ttf') format('truetype');
  font-weight: normal;
}

@font-face {
  font-family: OpenSans;
  src: url('./fonts/OpenSans-Bold.ttf') format('truetype');
  font-weight: bold;
}

.header {
  display: flex;
  flex-direction: row;
  gap: 0.8em;
  min-width: 14.6875rem;
  margin-top: 8px;
  margin-left: auto;
}

.header-container {
  justify-content: flex-start;
}

.receipt-header {
  letter-spacing: -0.5px;
}

.receipt-date-label {
  letter-spacing: -0.25px;
}

.receipt-value-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 7px;
}

.receipt-date-value-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
}

.body {
  margin-top: 3.0313rem;
  margin-left: 8px;
}

.receipt-owner-name {
  margin-top: 3.5px;
  letter-spacing: 0.25px;
}

.receipt-owner-adress,
.customer-owner-address {
  margin-top: 3.5px;
  letter-spacing: 0.25px;
}

.receipt-owner-oib-header,
.customer-owner-oib-header {
  margin-top: 5.5px;
}

.receipt-owner-oib,
.customer-owner-oib {
  margin-top: 3px;
}

.buyer-info-container {
  margin-left: auto;
  margin-top: 0.5px;
  width: 235px;
}

.buyer-info {
  letter-spacing: 0.25px;
  margin-top: 3px;
}

.table-container {
  display: flex;
  flex-direction: column;
  margin-top: 2.5313rem;
}

.table-header {
  flex-grow: 1;
  display: grid;
  /*  grid-template-columns: 25fr 270fr 40fr 60fr 20fr 35fr 60fr;  */
  grid-template-columns: 4.5% 51% 8% 12% 7% 7% auto;
  padding-top: 0;
  padding-left: 0;
  padding-bottom: 0;
  margin-top: 9.5px;
  margin-left: 8.5px;
  margin-bottom: 7px;
  margin-right: 5px;
}

.table-header div:last-child {
  justify-self: end;
}

.table-body {
  display: flex;
  flex-direction: column;
}

.table-body-item {
  flex-grow: 1;
  display: grid;
  grid-template-columns: 5% 51% 8% 12% 7% 7% auto;
  padding-top: 8.5px;
  padding-bottom: 18px;
  padding-left: 5px;
  padding-right: 5px;
}

.table-body-item div:last-child {
  justify-self: end;
}

.total-container {
  margin-top: 18px;
}

.total {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
}

.total-label-style {
  flex-grow: 35;
  flex-basis: 0;
  text-align: end;
}

.total-value-container {
  flex-grow: 10;
  flex-basis: 0;
  text-align: end;
  margin-right: 5px;
}

.info-container {
  margin-left: 5px;
  padding-top: 2px;
}

.info-item {
  margin-bottom: 9px;
}

.info-item-label {
  color: rgb(85, 85, 85);
}

.info-item-value {
  margin-top: 3px;
}

.info-data-container {
  display: flex;
  flex-wrap: nowrap;
  margin-top: auto;
}

.info-item-container {
  flex-grow: 1;
  color: rgb(85, 85, 85);
}

.info-item-container:last-child {
  flex-shrink: 1;
}

.bottom-info-container {
  margin-top: 20px;
}

.footer {
  position: absolute;
  bottom: 0;
}

#qrcode {
  height: 75px !important;
  width: 75px !important;
}
</style>
